import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export const reportService = {
  // Get all report templates
  async getTemplates() {
    const { data, error } = await supabase
      ?.from('report_templates')
      ?.select('*')
      ?.order('is_default', { ascending: false })
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get single template
  async getTemplate(templateId) {
    const { data, error } = await supabase
      ?.from('report_templates')
      ?.select('*')
      ?.eq('id', templateId)
      ?.single();

    if (error) throw error;
    return data;
  },

  // Create new template
  async createTemplate(templateData) {
    const { data: userData } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase
      ?.from('report_templates')
      ?.insert({
        template_name: templateData?.templateName,
        description: templateData?.description,
        metrics: templateData?.metrics,
        date_range_type: templateData?.dateRangeType,
        filters: templateData?.filters || {},
        created_by: userData?.user?.id,
        is_default: templateData?.isDefault || false
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Update template
  async updateTemplate(templateId, templateData) {
    const { data, error } = await supabase
      ?.from('report_templates')
      ?.update({
        template_name: templateData?.templateName,
        description: templateData?.description,
        metrics: templateData?.metrics,
        date_range_type: templateData?.dateRangeType,
        filters: templateData?.filters,
        is_default: templateData?.isDefault,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', templateId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Delete template
  async deleteTemplate(templateId) {
    const { error } = await supabase
      ?.from('report_templates')
      ?.delete()
      ?.eq('id', templateId);

    if (error) throw error;
  },

  // Generate report data
  async generateReportData(startDate, endDate, metrics) {
    const { data, error } = await supabase
      ?.rpc('get_performance_report_data', {
        start_date: startDate,
        end_date: endDate,
        selected_metrics: metrics
      });

    if (error) throw error;
    return data || {};
  },

  // Save report to history
  async saveReportHistory(reportData) {
    const { data: userData } = await supabase?.auth?.getUser();
    
    const { data, error } = await supabase
      ?.from('report_history')
      ?.insert({
        template_id: reportData?.templateId,
        report_name: reportData?.reportName,
        metrics: reportData?.metrics,
        date_range_start: reportData?.dateRangeStart,
        date_range_end: reportData?.dateRangeEnd,
        filters: reportData?.filters || {},
        generated_by: userData?.user?.id,
        file_format: reportData?.fileFormat
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Get report history
  async getReportHistory(limit = 20) {
    const { data, error } = await supabase
      ?.from('report_history')
      ?.select('*')
      ?.order('generated_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;
    return data || [];
  },

  // Export to CSV
  exportToCSV(reportData, reportName, startDate, endDate) {
    const rows = [];
    
    // Header
    rows?.push(['Report Performance - CLICCAMI.NET']);
    rows?.push(['Report:', reportName]);
    rows?.push(['Periodo:', `${format(new Date(startDate), 'dd/MM/yyyy', { locale: it })} - ${format(new Date(endDate), 'dd/MM/yyyy', { locale: it })}`]);
    rows?.push(['Generato:', format(new Date(), 'dd/MM/yyyy HH:mm', { locale: it })]);
    rows?.push([]);

    // Summary metrics
    if (reportData?.total_stores !== undefined) {
      rows?.push(['Metriche Generali']);
      rows?.push(['Metrica', 'Valore']);
      if (reportData?.total_stores !== undefined) rows?.push(['Vetrine Totali', reportData?.total_stores]);
      if (reportData?.total_views !== undefined) rows?.push(['Visualizzazioni Totali', reportData?.total_views]);
      if (reportData?.total_saves !== undefined) rows?.push(['Salvataggi Totali', reportData?.total_saves]);
      if (reportData?.total_promotions !== undefined) rows?.push(['Promozioni Attive', reportData?.total_promotions]);
      if (reportData?.total_engagement !== undefined) rows?.push(['Engagement Totale', reportData?.total_engagement]);
      if (reportData?.avg_views_per_store !== undefined) rows?.push(['Media Visualizzazioni per Vetrina', reportData?.avg_views_per_store]);
      if (reportData?.avg_saves_per_store !== undefined) rows?.push(['Media Salvataggi per Vetrina', reportData?.avg_saves_per_store]);
      rows?.push([]);
    }

    // Category performance
    if (reportData?.category_performance && reportData?.category_performance?.length > 0) {
      rows?.push(['Performance per Categoria']);
      rows?.push(['Categoria', 'Numero Vetrine', 'Visualizzazioni', 'Salvataggi']);
      reportData?.category_performance?.forEach(cat => {
        rows?.push([
          this.getCategoryLabel(cat?.categoria),
          cat?.store_count,
          cat?.total_views,
          cat?.total_saves
        ]);
      });
      rows?.push([]);
    }

    // Store performance
    if (reportData?.store_performance && reportData?.store_performance?.length > 0) {
      rows?.push(['Performance Vetrine (Top 50)']);
      rows?.push(['Negozio', 'Categoria', 'Zona', 'Visualizzazioni', 'Salvataggi', 'Engagement Promo']);
      reportData?.store_performance?.forEach(store => {
        rows?.push([
          store?.nome_negozio,
          this.getCategoryLabel(store?.categoria),
          store?.zona,
          store?.view_count,
          store?.save_count,
          store?.promotion_engagement
        ]);
      });
    }

    // Convert to CSV string
    const csvContent = rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))?.join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `report_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  },

  // Export to PDF
  exportToPDF(reportData, reportName, startDate, endDate) {
    const doc = new jsPDF();
    let yPosition = 20;

    // Header
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Report Performance', 105, yPosition, { align: 'center' });
    yPosition += 10;
    
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'normal');
    doc?.text('CLICCAMI.NET', 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Report info
    doc?.setFontSize(10);
    doc?.text(`Report: ${reportName}`, 14, yPosition);
    yPosition += 7;
    doc?.text(`Periodo: ${format(new Date(startDate), 'dd/MM/yyyy', { locale: it })} - ${format(new Date(endDate), 'dd/MM/yyyy', { locale: it })}`, 14, yPosition);
    yPosition += 7;
    doc?.text(`Generato: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: it })}`, 14, yPosition);
    yPosition += 12;

    // Summary metrics
    if (reportData?.total_stores !== undefined) {
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Metriche Generali', 14, yPosition);
      yPosition += 7;

      const summaryData = [];
      if (reportData?.total_stores !== undefined) summaryData?.push(['Vetrine Totali', reportData?.total_stores]);
      if (reportData?.total_views !== undefined) summaryData?.push(['Visualizzazioni Totali', reportData?.total_views]);
      if (reportData?.total_saves !== undefined) summaryData?.push(['Salvataggi Totali', reportData?.total_saves]);
      if (reportData?.total_promotions !== undefined) summaryData?.push(['Promozioni Attive', reportData?.total_promotions]);
      if (reportData?.total_engagement !== undefined) summaryData?.push(['Engagement Totale', reportData?.total_engagement]);
      if (reportData?.avg_views_per_store !== undefined) summaryData?.push(['Media Visualizzazioni/Vetrina', reportData?.avg_views_per_store]);
      if (reportData?.avg_saves_per_store !== undefined) summaryData?.push(['Media Salvataggi/Vetrina', reportData?.avg_saves_per_store]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Metrica', 'Valore']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [255, 107, 107] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc?.lastAutoTable?.finalY + 10;
    }

    // Category performance
    if (reportData?.category_performance && reportData?.category_performance?.length > 0) {
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Performance per Categoria', 14, yPosition);
      yPosition += 7;

      const categoryData = reportData?.category_performance?.map(cat => [
        this.getCategoryLabel(cat?.categoria),
        cat?.store_count,
        cat?.total_views,
        cat?.total_saves
      ]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Categoria', 'Vetrine', 'Visualizzazioni', 'Salvataggi']],
        body: categoryData,
        theme: 'striped',
        headStyles: { fillColor: [78, 205, 196] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc?.lastAutoTable?.finalY + 10;
    }

    // Store performance
    if (reportData?.store_performance && reportData?.store_performance?.length > 0) {
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Performance Vetrine (Top 50)', 14, yPosition);
      yPosition += 7;

      const storeData = reportData?.store_performance?.slice(0, 50)?.map(store => [
        store?.nome_negozio,
        this.getCategoryLabel(store?.categoria),
        store?.zona,
        store?.view_count,
        store?.save_count,
        store?.promotion_engagement
      ]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Negozio', 'Categoria', 'Zona', 'Views', 'Saves', 'Engagement']],
        body: storeData,
        theme: 'striped',
        headStyles: { fillColor: [69, 183, 209] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8 }
      });
    }

    // Save PDF
    doc?.save(`report_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
  },

  // Helper function for category labels
  getCategoryLabel(value) {
    const labels = {
      'bar-caffe': 'Bar & Caffè',
      'ristoranti-pizzerie': 'Ristoranti & Pizzerie',
      'abbigliamento-moda': 'Abbigliamento & Moda',
      'bellezza-benessere': 'Bellezza & Benessere',
      'casa-arredamento': 'Casa & Arredamento',
      'elettronica-tecnologia': 'Elettronica & Tecnologia',
      'salute-farmacia': 'Salute & Farmacia',
      'sport-tempo-libero': 'Sport & Tempo Libero',
      'servizi-professionali': 'Servizi Professionali'
    };
    return labels?.[value] || value;
  },

  // Available metrics list
  getAvailableMetrics() {
    return [
      { value: 'total_stores', label: 'Vetrine Totali', category: 'general' },
      { value: 'total_views', label: 'Visualizzazioni Totali', category: 'engagement' },
      { value: 'total_saves', label: 'Salvataggi Totali', category: 'engagement' },
      { value: 'total_promotions', label: 'Promozioni Attive', category: 'promotions' },
      { value: 'total_engagement', label: 'Engagement Totale', category: 'promotions' },
      { value: 'avg_views_per_store', label: 'Media Visualizzazioni per Vetrina', category: 'engagement' },
      { value: 'avg_saves_per_store', label: 'Media Salvataggi per Vetrina', category: 'engagement' },
      { value: 'category_performance', label: 'Performance per Categoria', category: 'detailed' },
      { value: 'store_performance', label: 'Performance Vetrine (Top 50)', category: 'detailed' }
    ];
  },

  // Date range presets
  getDateRangePresets() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return [
      {
        value: 'last_7_days',
        label: 'Ultimi 7 giorni',
        getRange: () => ({
          start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
          end: now
        })
      },
      {
        value: 'last_30_days',
        label: 'Ultimi 30 giorni',
        getRange: () => ({
          start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
          end: now
        })
      },
      {
        value: 'last_90_days',
        label: 'Ultimi 90 giorni',
        getRange: () => ({
          start: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000),
          end: now
        })
      },
      {
        value: 'this_month',
        label: 'Questo mese',
        getRange: () => ({
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: now
        })
      },
      {
        value: 'last_month',
        label: 'Mese scorso',
        getRange: () => {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          return { start: lastMonth, end: lastMonthEnd };
        }
      },
      {
        value: 'this_year',
        label: 'Quest\'anno',
        getRange: () => ({
          start: new Date(now.getFullYear(), 0, 1),
          end: now
        })
      },
      {
        value: 'custom',
        label: 'Personalizzato',
        getRange: null
      }
    ];
  }
};
